import type { StateCreator } from 'zustand';
import type {
  Project,
  PertNode,
  PertEdge,
  Member,
  ProjectSettings,
  ProjectRole,
  GanttTask,
  RaciEntry,
  WbsNode,
  StorageMode,
  Notification,
} from '../../types';
import { addWorkingDays } from '../../utils/calendar';
import {
  createBackendProject,
  fetchBackendProjects,
  transformBackendProject,
  createBackendNode,
  updateBackendNode,
  deleteBackendNode,
  createBackendEdge,
  updateBackendEdge,
  deleteBackendEdge,
  createBackendMember,
  updateBackendMember,
  deleteBackendMember,
  updateBackendProject,
  deleteBackendProject,
} from '../../api/projects';
import { reviveProject } from '../utils/project';

// ============================================================================
// PROJECT SLICE - Manages project data (nodes, edges, members, etc.)
// ============================================================================

export interface ProjectSlice {
  // Current project
  currentProject: Project | null;
  projects: Project[];

  // PERT data
  nodes: PertNode[];
  edges: PertEdge[];

  // Members
  members: Member[];

  // View-specific data
  ganttTasks: GanttTask[];
  raciMatrix: RaciEntry[];
  wbsTree: WbsNode[];

  // Project CRUD
  createProject: (name: string, description: string, startDate?: Date, clientId?: string) => Promise<Project | null>;
  loadProject: (id: string) => void;
  updateProject: (updates: Partial<Project>) => void;
  deleteProject: (id: string) => void;
  setCurrentProject: (project: Project | null) => void;
  getProjectsByClient: (clientId: string) => Project[];
  syncWithBackend: () => Promise<void>;

  // Undo/Redo snapshot
  restoreSnapshot: (snapshot: { nodes: PertNode[]; edges: PertEdge[] }) => void;

  // Node operations
  addNode: (node: Partial<PertNode>) => string;
  updateNode: (id: string, updates: Partial<PertNode>) => void;
  deleteNode: (id: string) => void;
  moveNode: (id: string, x: number, y: number) => void;
  getNode: (id: string) => PertNode | undefined;

  // Edge operations
  addEdge: (edge: Partial<PertEdge>) => string;
  updateEdge: (id: string, updates: Partial<PertEdge>) => void;
  deleteEdge: (id: string) => void;
  getEdge: (id: string) => PertEdge | undefined;
  getEdgesForNode: (nodeId: string) => { incoming: PertEdge[]; outgoing: PertEdge[] };

  // Member operations
  addMember: (member: Omit<Member, 'id'>) => string;
  updateMember: (id: string, updates: Partial<Member>) => void;
  deleteMember: (id: string) => void;

  // GANTT operations
  generateGanttTasks: () => void;
  updateGanttTask: (id: string, updates: Partial<GanttTask>) => void;

  // RACI operations
  generateRaciMatrix: () => void;
  updateRaciEntry: (taskId: string, memberId: string, role: 'R' | 'A' | 'C' | 'I' | null) => void;
  getRaciRole: (taskId: string, memberId: string) => 'R' | 'A' | 'C' | 'I' | null;

  // WBS operations
  generateWbsTree: () => void;
  updateWbsNode: (id: string, updates: Partial<WbsNode>) => void;

  // Optional cross-slice helpers
  storageMode?: StorageMode;
  addNotification?: (type: Notification['type'], message: string) => void;
}

const defaultSettings: ProjectSettings = {
  workingHoursPerDay: 8,
  workingDaysPerWeek: 5,
  currency: 'EUR',
  timezone: 'Europe/Paris',
  dateFormat: 'dd/MM/yyyy',
  includeWeekends: true,
  holidays: [],
};

const roleWeight: Record<ProjectRole, number> = {
  VIEWER: 0,
  COMMENTATOR: 1,
  EDITOR: 2,
  ADMIN: 3,
  OWNER: 4,
};

const hasMinimumRole = (
  project: Project | null,
  storageMode: StorageMode | undefined,
  minimumRole: ProjectRole
): boolean => {
  if (!project?.currentUserRole) {
    return storageMode !== 'backend';
  }
  return roleWeight[project.currentUserRole] >= roleWeight[minimumRole];
};

export const createProjectSlice: StateCreator<ProjectSlice> = (set, get, _api) => ({
  // Initial state
  currentProject: null,
  projects: [],
  nodes: [],
  edges: [],
  members: [],
  ganttTasks: [],
  raciMatrix: [],
  wbsTree: [],

  // Project CRUD
  createProject: async (name, description, startDate = new Date(), clientId) => {
    if (get().storageMode === 'backend') {
      try {
        const created = await createBackendProject({
          name,
          description,
          startDate,
          clientId,
        });
        const hydrated = reviveProject(transformBackendProject(created));
        set((state) => ({
          projects: [hydrated, ...state.projects.filter((project) => project.id !== hydrated.id)],
          currentProject: hydrated,
          nodes: hydrated.nodes || [],
          edges: hydrated.edges || [],
          members: hydrated.members || [],
        }));
        get().addNotification?.('success', 'Projet créé sur le serveur');
        return hydrated;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Erreur inconnue';
        get().addNotification?.('error', `Impossible de créer le projet : ${message}`);
        throw error;
      }
    }

    const newProject: Project = {
      id: `proj-${Date.now()}`,
      name,
      description,
      clientId,
      nodes: [],
      edges: [],
      members: [],
      startDate,
      settings: defaultSettings,
      currentUserRole: 'OWNER',
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    set((state) => ({
      projects: [...state.projects, newProject],
      currentProject: newProject,
      nodes: [],
      edges: [],
      members: [],
    }));

    return newProject;
  },

  loadProject: (id) => {
    const project = get().projects.find((p) => p.id === id);
    if (project) {
      set({
        currentProject: project,
        nodes: project.nodes,
        edges: project.edges,
        members: project.members,
      });
    }
  },

  updateProject: (updates) => {
    if (!hasMinimumRole(get().currentProject, get().storageMode, 'ADMIN')) {
      get().addNotification?.('warning', 'Droits insuffisants pour modifier le projet.');
      return;
    }

    // Optimistic update local immédiatement
    const previousProject = get().currentProject;
    set((state) => {
      if (!state.currentProject) return state;
      const updatedProject = { ...state.currentProject, ...updates, updatedAt: new Date() };
      return {
        currentProject: updatedProject,
        projects: state.projects.map((p) => p.id === updatedProject.id ? updatedProject : p),
      };
    });

    // Sync backend en mode backend
    if (get().storageMode === 'backend' && previousProject?.id) {
      void updateBackendProject(previousProject.id, {
        name:        updates.name,
        description: updates.description,
        clientId:    updates.clientId,
        startDate:   updates.startDate,
        endDate:     updates.endDate,
        settings:    updates.settings as Record<string, unknown> | undefined,
      }).catch((err: unknown) => {
        // Rollback en cas d'erreur backend
        if (previousProject) {
          set((state) => ({
            currentProject: state.currentProject?.id === previousProject.id ? previousProject : state.currentProject,
            projects: state.projects.map((p) => p.id === previousProject.id ? previousProject : p),
          }));
        }
        get().addNotification?.('error', `Échec de la mise à jour : ${err instanceof Error ? err.message : 'Erreur inconnue'}`);
      });
    }
  },

  deleteProject: (id) => {
    if (!hasMinimumRole(get().currentProject, get().storageMode, 'ADMIN')) {
      get().addNotification?.('warning', 'Droits insuffisants pour supprimer le projet.');
      return;
    }

    // Snapshot pour rollback
    const deletedProject = get().projects.find((p) => p.id === id);

    // Optimistic update local
    set((state) => ({
      projects: state.projects.filter((p) => p.id !== id),
      currentProject: state.currentProject?.id === id ? null : state.currentProject,
    }));

    // Sync backend en mode backend
    if (get().storageMode === 'backend') {
      void deleteBackendProject(id).catch((err: unknown) => {
        // Rollback : remettre le projet supprimé
        if (deletedProject) {
          set((state) => ({
            projects: [...state.projects, deletedProject],
            currentProject: state.currentProject ?? deletedProject,
          }));
        }
        get().addNotification?.('error', `Échec de la suppression : ${err instanceof Error ? err.message : 'Erreur inconnue'}`);
      });
    }
  },

  setCurrentProject: (project) => {
    set({
      currentProject: project,
      nodes: project?.nodes || [],
      edges: project?.edges || [],
      members: project?.members || [],
    });
  },

  getProjectsByClient: (clientId) => {
    return get().projects.filter((p) => p.clientId === clientId);
  },

  syncWithBackend: async () => {
    if (get().storageMode !== 'backend') {
      return;
    }
    try {
      const remoteProjects = await fetchBackendProjects();
      const hydrated = remoteProjects.map((project) => reviveProject(transformBackendProject(project)));
      set((state) => {
        const currentId = state.currentProject?.id;
        const nextCurrent = currentId
          ? hydrated.find((project) => project.id === currentId) || hydrated[0] || null
          : hydrated[0] || null;

        return {
          projects: hydrated,
          currentProject: nextCurrent,
          nodes: nextCurrent?.nodes || [],
          edges: nextCurrent?.edges || [],
          members: nextCurrent?.members || [],
        };
      });
      if (hydrated.length === 0) {
        get().addNotification?.('info', 'Aucun projet distant disponible');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur inconnue';
      get().addNotification?.('error', `Synchronisation impossible : ${message}`);
      throw error;
    }
  },

  // Undo/Redo snapshot
  restoreSnapshot: (snapshot) => {
    set((state) => {
      const { nodes, edges } = snapshot;
      const currentProject = state.currentProject
        ? { ...state.currentProject, nodes, edges }
        : state.currentProject;
      const projects =
        state.currentProject && currentProject
          ? state.projects.map((p) => (p.id === currentProject.id ? { ...p, nodes, edges } : p))
          : state.projects;
      return { nodes, edges, currentProject, projects };
    });
  },

  // Node operations
  addNode: (node) => {
    if (!hasMinimumRole(get().currentProject, get().storageMode, 'EDITOR')) {
      get().addNotification?.('warning', 'Droits insuffisants pour créer un nœud.');
      return '';
    }
    (get() as any).pushHistory?.('addNode', { nodes: get().nodes, edges: get().edges });
    const newNode: PertNode = {
      id: `node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: node.name || 'New Node',
      description: node.description,
      x: node.x || 100,
      y: node.y || 100,
      earlyStart: 0,
      earlyFinish: 0,
      lateStart: 0,
      lateFinish: 0,
      slack: 0,
      isCritical: false,
      type: node.type || 'task',
      assignedTo: node.assignedTo || [],
      resources: node.resources || [],
      progress: node.progress || 0,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    set((state) => {
      const nodes = [...state.nodes, newNode];
      const currentProject = state.currentProject ? { ...state.currentProject, nodes } : state.currentProject;
      const projects =
        state.currentProject && currentProject
          ? state.projects.map((project) =>
              project.id === currentProject.id ? { ...project, nodes } : project
            )
          : state.projects;
      return { nodes, currentProject, projects };
    });

    if (get().storageMode === 'backend') {
      const projectId = get().currentProject?.id;
      if (projectId) {
        createBackendNode(projectId, {
          id: newNode.id,
          name: newNode.name,
          description: newNode.description,
          data: newNode,
        }).catch((error) => {
          console.error('Erreur de création de nœud distant', error);
          get().addNotification?.('error', 'Impossible de sauvegarder le nœud sur le serveur');
          get().syncWithBackend?.();
        });
      }
    }

    return newNode.id;
  },

  updateNode: (id, updates) => {
    if (!hasMinimumRole(get().currentProject, get().storageMode, 'EDITOR')) {
      get().addNotification?.('warning', 'Droits insuffisants pour modifier un nœud.');
      return;
    }
    (get() as any).pushHistory?.('updateNode', { nodes: get().nodes, edges: get().edges });
    const updatedAt = new Date();
    set((state) => {
      const nodes = state.nodes.map((n) => (n.id === id ? { ...n, ...updates, updatedAt } : n));
      const currentProject = state.currentProject ? { ...state.currentProject, nodes } : state.currentProject;
      const projects =
        state.currentProject && currentProject
          ? state.projects.map((project) =>
              project.id === currentProject.id ? { ...project, nodes } : project
            )
          : state.projects;
      return { nodes, currentProject, projects };
    });

    if (get().storageMode === 'backend') {
      const projectId = get().currentProject?.id;
      const target = get().nodes.find((n) => n.id === id);
      if (projectId && target) {
        updateBackendNode(projectId, id, {
          name: target.name,
          description: target.description,
          data: target,
        }).catch((error) => {
          console.error('Erreur de mise à jour de nœud distant', error);
          get().addNotification?.('error', 'Impossible de mettre à jour le nœud sur le serveur');
          get().syncWithBackend?.();
        });
      }
    }
  },

  deleteNode: (id) => {
    if (!hasMinimumRole(get().currentProject, get().storageMode, 'EDITOR')) {
      get().addNotification?.('warning', 'Droits insuffisants pour supprimer un nœud.');
      return;
    }
    (get() as any).pushHistory?.('deleteNode', { nodes: get().nodes, edges: get().edges });
    set((state) => {
      const nodes = state.nodes.filter((n) => n.id !== id);
      const edges = state.edges.filter((e) => e.from !== id && e.to !== id);
      const currentProject = state.currentProject
        ? { ...state.currentProject, nodes, edges }
        : state.currentProject;
      const projects =
        state.currentProject && currentProject
          ? state.projects.map((project) =>
              project.id === currentProject.id ? { ...project, nodes, edges } : project
            )
          : state.projects;
      return { nodes, edges, currentProject, projects };
    });

    if (get().storageMode === 'backend') {
      const projectId = get().currentProject?.id;
      if (projectId) {
        deleteBackendNode(projectId, id).catch((error) => {
          console.error('Erreur de suppression de nœud distant', error);
          get().addNotification?.('error', 'Impossible de supprimer le nœud sur le serveur');
          get().syncWithBackend?.();
        });
      }
    }
  },

  moveNode: (id, x, y) => {
    get().updateNode(id, { x, y });
  },

  getNode: (id) => get().nodes.find((n) => n.id === id),

  // Edge operations
  addEdge: (edge) => {
    if (!hasMinimumRole(get().currentProject, get().storageMode, 'EDITOR')) {
      get().addNotification?.('warning', 'Droits insuffisants pour créer un lien.');
      return '';
    }
    (get() as any).pushHistory?.('addEdge', { nodes: get().nodes, edges: get().edges });
    const newEdge: PertEdge = {
      id: `edge-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      from: edge.from!,
      to: edge.to!,
      taskName: edge.taskName || 'New Task',
      duration: edge.duration || 1,
      isCritical: false,
      dependencyType: edge.dependencyType || 'FS',
      lag: edge.lag || 0,
      description: edge.description,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    set((state) => {
      const edges = [...state.edges, newEdge];
      const currentProject = state.currentProject ? { ...state.currentProject, edges } : state.currentProject;
      const projects =
        state.currentProject && currentProject
          ? state.projects.map((project) =>
              project.id === currentProject.id ? { ...project, edges } : project
            )
          : state.projects;
      return { edges, currentProject, projects };
    });

    if (get().storageMode === 'backend') {
      const projectId = get().currentProject?.id;
      if (projectId) {
        createBackendEdge(projectId, {
          id: newEdge.id,
          fromNodeId: newEdge.from,
          toNodeId: newEdge.to,
          data: newEdge,
        }).catch((error) => {
          console.error('Erreur de création de liaison distante', error);
          get().addNotification?.('error', 'Impossible de sauvegarder la liaison sur le serveur');
          get().syncWithBackend?.();
        });
      }
    }

    return newEdge.id;
  },

  updateEdge: (id, updates) => {
    if (!hasMinimumRole(get().currentProject, get().storageMode, 'EDITOR')) {
      get().addNotification?.('warning', 'Droits insuffisants pour modifier un lien.');
      return;
    }
    (get() as any).pushHistory?.('updateEdge', { nodes: get().nodes, edges: get().edges });
    const updatedAt = new Date();
    set((state) => {
      const edges = state.edges.map((e) => (e.id === id ? { ...e, ...updates, updatedAt } : e));
      const currentProject = state.currentProject ? { ...state.currentProject, edges } : state.currentProject;
      const projects =
        state.currentProject && currentProject
          ? state.projects.map((project) =>
              project.id === currentProject.id ? { ...project, edges } : project
            )
          : state.projects;
      return { edges, currentProject, projects };
    });

    if (get().storageMode === 'backend') {
      const projectId = get().currentProject?.id;
      const target = get().edges.find((e) => e.id === id);
      if (projectId && target) {
        updateBackendEdge(projectId, id, {
          fromNodeId: target.from,
          toNodeId: target.to,
          data: target,
        }).catch((error) => {
          console.error('Erreur de mise à jour de liaison distante', error);
          get().addNotification?.('error', 'Impossible de mettre à jour la liaison sur le serveur');
          get().syncWithBackend?.();
        });
      }
    }
  },

  deleteEdge: (id) => {
    if (!hasMinimumRole(get().currentProject, get().storageMode, 'EDITOR')) {
      get().addNotification?.('warning', 'Droits insuffisants pour supprimer un lien.');
      return;
    }
    (get() as any).pushHistory?.('deleteEdge', { nodes: get().nodes, edges: get().edges });
    set((state) => {
      const edges = state.edges.filter((e) => e.id !== id);
      const currentProject = state.currentProject ? { ...state.currentProject, edges } : state.currentProject;
      const projects =
        state.currentProject && currentProject
          ? state.projects.map((project) =>
              project.id === currentProject.id ? { ...project, edges } : project
            )
          : state.projects;
      return { edges, currentProject, projects };
    });

    if (get().storageMode === 'backend') {
      const projectId = get().currentProject?.id;
      if (projectId) {
        deleteBackendEdge(projectId, id).catch((error) => {
          console.error('Erreur de suppression de liaison distante', error);
          get().addNotification?.('error', 'Impossible de supprimer la liaison sur le serveur');
          get().syncWithBackend?.();
        });
      }
    }
  },

  getEdge: (id) => get().edges.find((e) => e.id === id),

  getEdgesForNode: (nodeId) => {
    const edges = get().edges;
    return {
      incoming: edges.filter((e) => e.to === nodeId),
      outgoing: edges.filter((e) => e.from === nodeId),
    };
  },

  // Member operations
  addMember: (member) => {
    if (!hasMinimumRole(get().currentProject, get().storageMode, 'EDITOR')) {
      get().addNotification?.('warning', 'Droits insuffisants pour ajouter un membre.');
      return '';
    }
    const newMember: Member = {
      ...member,
      id: `member-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };

    set((state) => {
      const members = [...state.members, newMember];
      const currentProject = state.currentProject ? { ...state.currentProject, members } : state.currentProject;
      const projects =
        state.currentProject && currentProject
          ? state.projects.map((project) =>
              project.id === currentProject.id ? { ...project, members } : project
            )
          : state.projects;
      return { members, currentProject, projects };
    });

    if (get().storageMode === 'backend') {
      const projectId = get().currentProject?.id;
      if (projectId) {
        createBackendMember(projectId, {
          id: newMember.id,
          name: newMember.name,
          email: newMember.email,
          role: newMember.role,
          data: newMember,
        }).catch((error) => {
          console.error('Erreur de création de membre distant', error);
          get().addNotification?.('error', 'Impossible de sauvegarder le membre sur le serveur');
          get().syncWithBackend?.();
        });
      }
    }

    return newMember.id;
  },

  updateMember: (id, updates) => {
    if (!hasMinimumRole(get().currentProject, get().storageMode, 'EDITOR')) {
      get().addNotification?.('warning', 'Droits insuffisants pour modifier un membre.');
      return;
    }
    set((state) => {
      const members = state.members.map((m) => (m.id === id ? { ...m, ...updates } : m));
      const currentProject = state.currentProject ? { ...state.currentProject, members } : state.currentProject;
      const projects =
        state.currentProject && currentProject
          ? state.projects.map((project) =>
              project.id === currentProject.id ? { ...project, members } : project
            )
          : state.projects;
      return { members, currentProject, projects };
    });

    if (get().storageMode === 'backend') {
      const projectId = get().currentProject?.id;
      const member = get().members.find((m) => m.id === id);
      if (projectId && member) {
        updateBackendMember(projectId, id, {
          name: member.name,
          email: member.email,
          role: member.role,
          data: member,
        }).catch((error) => {
          console.error('Erreur de mise à jour de membre distant', error);
          get().addNotification?.('error', 'Impossible de mettre à jour le membre sur le serveur');
          get().syncWithBackend?.();
        });
      }
    }
  },

  deleteMember: (id) => {
    if (!hasMinimumRole(get().currentProject, get().storageMode, 'EDITOR')) {
      get().addNotification?.('warning', 'Droits insuffisants pour supprimer un membre.');
      return;
    }
    set((state) => {
      const members = state.members.filter((m) => m.id !== id);
      const currentProject = state.currentProject ? { ...state.currentProject, members } : state.currentProject;
      const projects =
        state.currentProject && currentProject
          ? state.projects.map((project) =>
              project.id === currentProject.id ? { ...project, members } : project
            )
          : state.projects;
      return { members, currentProject, projects };
    });

    if (get().storageMode === 'backend') {
      const projectId = get().currentProject?.id;
      if (projectId) {
        deleteBackendMember(projectId, id).catch((error) => {
          console.error('Erreur de suppression de membre distant', error);
          get().addNotification?.('error', 'Impossible de supprimer le membre sur le serveur');
          get().syncWithBackend?.();
        });
      }
    }
  },

  // GANTT operations
  generateGanttTasks: () => {
    const { nodes, edges, currentProject } = get();

    if (!currentProject) return;

    const settings = currentProject.settings || defaultSettings;
    const includeWeekends = settings.includeWeekends ?? true;
    const holidays = settings.holidays ?? [];
    const projectStart = currentProject.startDate ? new Date(currentProject.startDate) : new Date();

    const ganttTasks: GanttTask[] = nodes.map((node) => {
      const startDate = addWorkingDays(projectStart, node.earlyStart, includeWeekends, holidays);
      const endDate = addWorkingDays(projectStart, node.earlyFinish, includeWeekends, holidays);

      const dependencies = edges
        .filter((e) => e.to === node.id)
        .map((e) => e.from);

      return {
        id: `gantt-${node.id}`,
        nodeId: node.id,
        name: node.name,
        startDate,
        endDate,
        progress: node.progress || 0,
        dependencies,
        assignedTo: node.assignedTo || [],
        isCritical: node.isCritical,
        milestone: node.type === 'milestone',
      };
    });

    set({ ganttTasks });
  },

  updateGanttTask: (id, updates) => {
    if (!hasMinimumRole(get().currentProject, get().storageMode, 'EDITOR')) {
      get().addNotification?.('warning', 'Droits insuffisants pour modifier le planning.');
      return;
    }
    set((state) => ({
      ganttTasks: state.ganttTasks.map((t) => (t.id === id ? { ...t, ...updates } : t)),
    }));
  },

  // RACI operations
  generateRaciMatrix: () => {
    const { nodes, members } = get();

    // Helper: Check if member role matches task context
    const isRoleRelevant = (memberRole: string, taskName: string): 'high' | 'medium' | 'low' => {
      const taskLower = taskName.toLowerCase();
      const roleLower = memberRole.toLowerCase();

      // High relevance - direct match
      if (
        (roleLower.includes('dev') && (taskLower.includes('dév') || taskLower.includes('dev') || taskLower.includes('intégration') || taskLower.includes('backend') || taskLower.includes('frontend'))) ||
        (roleLower.includes('design') && (taskLower.includes('ui') || taskLower.includes('ux') || taskLower.includes('design') || taskLower.includes('conception'))) ||
        (roleLower.includes('devops') && (taskLower.includes('déploiement') || taskLower.includes('deploy') || taskLower.includes('infra'))) ||
        (roleLower.includes('chef') && (taskLower.includes('analyse') || taskLower.includes('test') || taskLower.includes('recette')))
      ) {
        return 'high';
      }

      // Medium relevance - indirect match
      if (
        (roleLower.includes('dev') && (taskLower.includes('test') || taskLower.includes('conception'))) ||
        (roleLower.includes('design') && taskLower.includes('analyse')) ||
        (roleLower.includes('chef') && true) // Project manager relevant to all tasks
      ) {
        return 'medium';
      }

      return 'low';
    };

    const ensureAccountable = (
      assignments: { memberId: string; role: 'R' | 'A' | 'C' | 'I' }[],
      node: { assignedTo?: string[] }
    ) => {
      if (assignments.some((a) => a.role === 'A')) return;
      const manager = members.find((member) => {
        const roleLower = member.role.toLowerCase();
        return roleLower.includes('chef') || roleLower.includes('manager') || roleLower.includes('lead');
      });
      const fallbackId = node.assignedTo?.[0] ?? manager?.id ?? members[0]?.id;
      if (fallbackId) {
        assignments.push({ memberId: fallbackId, role: 'A' });
      }
    };

    // Generate RACI matrix with intelligent role assignments
    const raciMatrix: RaciEntry[] = nodes
      .filter((node) => node.type === 'task' || node.type === 'milestone')
      .map((node) => {
        const assignments: { memberId: string; role: 'R' | 'A' | 'C' | 'I' }[] = [];

        // If node has explicit assignments
        if (node.assignedTo && node.assignedTo.length > 0) {
          // First assignee is Accountable (A) and Responsible (R)
          assignments.push({ memberId: node.assignedTo[0], role: 'A' });
          assignments.push({ memberId: node.assignedTo[0], role: 'R' });

          // Additional assignees are Responsible (R)
          for (let i = 1; i < node.assignedTo.length; i++) {
            assignments.push({ memberId: node.assignedTo[i], role: 'R' });
          }

          const assignedMemberIds = new Set(node.assignedTo);

          // Assign roles to non-assigned members based on relevance
          members
            .filter((m) => !assignedMemberIds.has(m.id))
            .forEach((member) => {
              const relevance = isRoleRelevant(member.role, node.name);

              if (relevance === 'high') {
                // Highly relevant members should be Consulted
                assignments.push({ memberId: member.id, role: 'C' });
              } else if (relevance === 'medium') {
                // Medium relevance - Consulted or Informed based on role
                const isManager = member.role.toLowerCase().includes('chef');
                assignments.push({ memberId: member.id, role: isManager ? 'C' : 'I' });
              } else {
                // Low relevance - just Informed
                assignments.push({ memberId: member.id, role: 'I' });
              }
            });
        } else {
          // No explicit assignments - use intelligent defaults
          members.forEach((member) => {
            const relevance = isRoleRelevant(member.role, node.name);
            const isManager = member.role.toLowerCase().includes('chef');

            if (isManager && relevance === 'high') {
              // Manager for relevant task - Accountable
              assignments.push({ memberId: member.id, role: 'A' });
            } else if (relevance === 'high') {
              // Highly relevant specialist - Responsible
              assignments.push({ memberId: member.id, role: 'R' });
            } else if (relevance === 'medium') {
              // Medium relevance - Consulted
              assignments.push({ memberId: member.id, role: 'C' });
            } else {
              // Low relevance - Informed
              assignments.push({ memberId: member.id, role: 'I' });
            }
          });
        }

        ensureAccountable(assignments, node);

        return {
          taskId: node.id,
          taskName: node.name,
          assignments,
        };
      });

    set({ raciMatrix });
  },

  updateRaciEntry: (taskId, memberId, role) => {
    if (!hasMinimumRole(get().currentProject, get().storageMode, 'EDITOR')) {
      get().addNotification?.('warning', 'Droits insuffisants pour modifier la matrice RACI.');
      return;
    }
    set((state) => {
      const raciMatrix = state.raciMatrix.map((entry) => {
        if (entry.taskId !== taskId) return entry;

        const existingAssignment = entry.assignments.find((a) => a.memberId === memberId);

        if (role === null) {
          // Remove assignment
          return {
            ...entry,
            assignments: entry.assignments.filter((a) => a.memberId !== memberId),
          };
        }

        if (existingAssignment) {
          // Update existing assignment
          return {
            ...entry,
            assignments: entry.assignments.map((a) =>
              a.memberId === memberId ? { ...a, role } : a
            ),
          };
        } else {
          // Add new assignment
          return {
            ...entry,
            assignments: [...entry.assignments, { memberId, role }],
          };
        }
      });

      return { raciMatrix };
    });
  },

  getRaciRole: (taskId, memberId) => {
    const entry = get().raciMatrix.find((e) => e.taskId === taskId);
    const assignment = entry?.assignments.find((a) => a.memberId === memberId);
    return assignment?.role || null;
  },

  // WBS operations
  generateWbsTree: () => {
    const { nodes, edges } = get();

    // Build WBS tree based on dependencies
    // For simplicity, this creates a flat structure with auto-generated codes
    const wbsTree: WbsNode[] = nodes.map((node, index) => {
      // Find parent (first predecessor)
      const parentEdge = edges.find((e) => e.to === node.id);
      const parent = parentEdge ? parentEdge.from : undefined;

      return {
        id: `wbs-${node.id}`,
        name: node.name,
        code: `${index + 1}`, // Simple numbering for now
        level: parent ? 1 : 0,
        parent,
        children: edges.filter((e) => e.from === node.id).map((e) => e.to),
        nodeRef: node.id,
        deliverables: [],
        description: node.description,
      };
    });

    set({ wbsTree });
  },

  updateWbsNode: (id, updates) => {
    if (!hasMinimumRole(get().currentProject, get().storageMode, 'EDITOR')) {
      get().addNotification?.('warning', 'Droits insuffisants pour modifier le WBS.');
      return;
    }
    set((state) => ({
      wbsTree: state.wbsTree.map((n) => (n.id === id ? { ...n, ...updates } : n)),
    }));
  },
});
